import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AppProviders } from '@/providers/AppProviders';
import { useAuth } from '@/providers/AuthProvider';
import { AppShell } from '@/components/layout/AppShell';
import { PageSpinner } from '@/components/spinner';
import { LoginPage } from '@/pages/Login';

const OverviewPage = lazy(() => import('@/pages/Overview'));
const CompaniesListPage = lazy(() => import('@/pages/companies/CompaniesList'));
const CompanyLayout = lazy(() => import('@/pages/companies/CompanyLayout'));
const CompanyUsers = lazy(() => import('@/pages/companies/CompanyUsers'));
const CompanyProjects = lazy(() => import('@/pages/companies/CompanyProjects'));
const CompanyCredits = lazy(() => import('@/pages/companies/CompanyCredits'));
const CompanyLicenses = lazy(() => import('@/pages/companies/CompanyLicenses'));
const CompanyActivity = lazy(() => import('@/pages/companies/CompanyActivity'));
const CompanyHealth = lazy(() => import('@/pages/companies/CompanyHealth'));
const CompanyRiskHistory = lazy(() => import('@/pages/companies/CompanyRiskHistory'));
const CompanyActions = lazy(() => import('@/pages/companies/CompanyActions'));
const KpisPage = lazy(() => import('@/pages/Kpis'));
const AtRiskPage = lazy(() => import('@/pages/AtRisk'));
const SegmentsPage = lazy(() => import('@/pages/Segments'));
const NotFoundPage = lazy(() =>
  import('@/pages/NotFound').then((m) => ({ default: m.NotFoundPage })),
);

function AuthGate() {
  const { status } = useAuth();

  if (status === 'loading') {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  if (status !== 'authenticated') {
    return <LoginPage />;
  }

  return (
    <AppShell>
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/companies" element={<CompaniesListPage />} />
          <Route path="/companies/:id" element={<CompanyLayout />}>
            <Route index element={<CompanyHealth />} />
            <Route path="users" element={<CompanyUsers />} />
            <Route path="projects" element={<CompanyProjects />} />
            <Route path="credits" element={<CompanyCredits />} />
            <Route path="licenses" element={<CompanyLicenses />} />
            <Route path="activity" element={<CompanyActivity />} />
            <Route path="risk" element={<CompanyRiskHistory />} />
            <Route path="actions" element={<CompanyActions />} />
          </Route>
          <Route path="/kpis" element={<KpisPage />} />
          <Route path="/at-risk" element={<AtRiskPage />} />
          <Route path="/segments" element={<SegmentsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

export function App() {
  return (
    <AppProviders>
      <AuthGate />
    </AppProviders>
  );
}
